import { type AppEnvironment, setAppEnvironment, setCollectionMode } from './environment';
import { getLogger, setAnalyticsLoggingEnabled } from './logger';
import type { ClientAuthProvider } from './auth/authProvider';
import { initCalled, setInitCalled } from './logEvent/logEvent';
import { setUserGuidPromise } from './metadata/frameStart';
import { initializeAuthState } from './auth/authState';
import { timed } from './util/timed';
import { tryToGetUploadConfig } from './network/getUploadConfig';
import { setCookiesEnabled } from './clientId';
import { saveWebSessionIdWhenActive } from './webSession/webSessionId';

const log = getLogger('initBlizzard');

/**
 * Configure Blizzard for logging.
 *
 * This must be called before logging any events through {@link logEvent}.
 *
 * This function is async. If you do not want to await it, prefix the call with `void`, i.e. `void
 * initBlizzard(...)`.
 */
export async function initBlizzard(args: {
  debugLoggingEnabled?: boolean;
  cookiesDisabled?: boolean;
  appEnvironment: AppEnvironment;
  clientAuthProvider?: ClientAuthProvider;
  userGuid?: Promise<string | null>;
  // To include webClientId inside WebPageOpen.
  // Note: The webClientId here is different from the clientId that is used inside Blizzard SDK (blizzard_client_id).
  webClientId?: Promise<string | undefined>;
  mode?: CollectionMode;
  cookiesArgs?: {
    customDomain?: string;
    expireDays?: number;
  };
  metricsAppId?: string;
}): Promise<void> {
  if (initCalled) {
    throw new Error('initBlizzard should not be called multiple times');
  }

  log.info('Initializing Blizzard', args);

  setInitCalled();

  // Enable logging (do this first so the rest of the logs appear)
  setAnalyticsLoggingEnabled(Boolean(args.debugLoggingEnabled));

  // Disable cookies if specified
  // Default customDomain and expireDays are all set in clientId.ts
  setCookiesEnabled(
    !args.cookiesDisabled,
    args.cookiesArgs?.customDomain,
    args.cookiesArgs?.expireDays
  );

  // save the webSessionId into cookies (when cookies is enabled) whenever the page is not idle
  // this method sets multiple eventListeners to detect activeness
  saveWebSessionIdWhenActive();

  // Set the static application environment
  setAppEnvironment(args.appEnvironment);
  setCollectionMode(args.mode ?? CollectionMode.PROD);

  // Set the authentication provider
  initializeAuthState(args.clientAuthProvider ?? null);

  // If there's a promise to return the user GUID, give it to the FrameStart to resolve later
  if (args.userGuid) {
    setUserGuidPromise(args.userGuid);
  }

  // Asynchronously block on those promises and emit how long they took to resolve.
  // Everything above is lightweight so this is the only long-running initialization we care about.
  // noinspection JSIgnoredPromiseFromCall
  await timed(async () => {
    await args.userGuid;
    await tryToGetUploadConfig(); // eagerly refresh the SCA token or wait for the first SSO token
    log.info('Blizzard initialized');
  }, 'init_duration_millis');
}

/** Blizzard collection mode (staging or prod) */
export enum CollectionMode {
  STAGE = 'STAGE',
  PROD = 'PROD',
}
